import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getAdminCost,
  getAdminStats,
  type AdminCostResponse,
  type AdminStatsResponse,
} from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import LangToggle from '@/components/LangToggle';

/**
 * Admin dashboard — operator-only. Lives outside the main app navigation
 * (no menu link); reach it by visiting `/admin` directly.
 *
 * Auth model: a one-time token prompt on first visit. Token is stored
 * in localStorage so subsequent visits skip the prompt. The token is
 * the same string set as ADMIN_HEALTH_TOKEN in the backend `.env`.
 *
 * No charts library — we draw the few inline SVG charts manually to
 * keep the bundle lean. The shapes are deliberately simple: one line
 * for the trend, one bar set for the hourly histogram.
 */

const TOKEN_KEY = 'mystic_admin_token';

export default function Admin() {
  const { t } = useTranslation();
  const [token, setToken] = useState<string>(() => {
    try { return localStorage.getItem(TOKEN_KEY) ?? ''; } catch { return ''; }
  });
  const [draftToken, setDraftToken] = useState('');
  const [data, setData] = useState<AdminStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async (tok: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminStats(tok);
      setData(res);
    } catch (e) {
      setError((e as Error).message);
      // If the token was wrong, force a re-prompt next render.
      if (String((e as Error).message).includes('401')) {
        try { localStorage.removeItem(TOKEN_KEY); } catch {}
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const saveToken = () => {
    const t = draftToken.trim();
    if (!t) return;
    try { localStorage.setItem(TOKEN_KEY, t); } catch {}
    setToken(t);
  };

  const signOut = () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
    setToken('');
    setData(null);
  };

  // ─── Token prompt ─────────────────────────────────────────────────────
  if (!token) {
    return (
      <main className="mx-auto max-w-md px-5 pb-24 pt-6">
        <div className="mb-3 flex justify-end">
          <LangToggle />
        </div>
        <h1 className="mb-3 text-center font-serif text-2xl text-gold-300">
          {t('admin.title')}
        </h1>
        <OrnateFrame className="card-glow">
          <label className="block">
            <span className="mb-2 block text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('admin.token_label')}
            </span>
            <input
              type="password"
              value={draftToken}
              onChange={(e) => setDraftToken(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveToken(); }}
              placeholder={t('admin.token_placeholder')}
              className="w-full rounded-lg border border-gold-500/30
                         bg-night-900/60 px-3 py-2 font-mono text-sm
                         text-gold-100 outline-none transition
                         placeholder:text-gold-300/35 focus:border-gold-400"
            />
          </label>
          <button
            type="button"
            onClick={saveToken}
            disabled={!draftToken.trim()}
            className="mt-4 btn-primary w-full disabled:opacity-40"
          >
            {t('admin.sign_in')}
          </button>
          {error && (
            <p className="mt-3 text-center text-xs text-rose-400">{error}</p>
          )}
        </OrnateFrame>
      </main>
    );
  }

  // ─── Dashboard ────────────────────────────────────────────────────────
  return (
    <main className="mx-auto max-w-3xl px-5 pb-24 pt-4">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h1 className="font-serif text-2xl text-gold-300">{t('admin.title')}</h1>
        <div className="flex items-center gap-3">
          <LangToggle />
          <button
            type="button"
            onClick={signOut}
            className="text-xs text-gold-300/60 underline-offset-4 hover:underline"
          >
            {t('admin.sign_out')}
          </button>
        </div>
      </header>

      {loading && <p className="text-center text-gold-300/60">{t('admin.loading')}</p>}
      {error && <p className="text-center text-xs text-rose-400">{error}</p>}

      {data && (
        <>
          <p className="mb-4 text-center text-[10px] uppercase tracking-widest text-gold-500/40">
            {t('admin.snapshot_at', { when: new Date(data.current.captured_at).toLocaleString() })}
          </p>

          {/* Top-line KPI grid */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Kpi label={t('admin.kpi_users_total')} value={data.current.users.total} />
            <Kpi label={t('admin.kpi_onboarded')} value={data.current.users.onboarded} />
            <Kpi label={t('admin.kpi_dau')} value={data.current.users.dau} />
            <Kpi label={t('admin.kpi_wau')} value={data.current.users.wau} />
            <Kpi label={t('admin.kpi_mau')} value={data.current.users.mau} />
            <Kpi label={t('admin.kpi_notif')} value={data.current.users.notifications_opted_in} />
          </div>

          <Section title={t('admin.section_subscription')}>
            <Kpi label={t('admin.kpi_priestess_active')} value={data.current.subscription.priestess_active} />
            <Kpi label={t('admin.kpi_expiring_7d')} value={data.current.subscription.priestess_expiring_7d}
                 tone={data.current.subscription.priestess_expiring_7d > 0 ? 'warn' : undefined} />
          </Section>

          <Section title={t('admin.section_revenue')}>
            <Kpi label={t('admin.kpi_h24')} value={data.current.revenue_stars.h24} />
            <Kpi label={t('admin.kpi_d7')} value={data.current.revenue_stars.d7} />
            <Kpi label={t('admin.kpi_d30')} value={data.current.revenue_stars.d30} />
            <Kpi label={t('admin.kpi_total')} value={data.current.revenue_stars.total} />
          </Section>

          <Section title={t('admin.section_streaks_referrals')}>
            <Kpi label={t('admin.kpi_streak_7')} value={data.current.streaks.above_7} />
            <Kpi label={t('admin.kpi_streak_30')} value={data.current.streaks.above_30} />
            <Kpi label={t('admin.kpi_referrals_total')} value={data.current.referrals.total} />
            <Kpi label={t('admin.kpi_referrals_converted')} value={data.current.referrals.converted} />
          </Section>

          <Section title={t('admin.section_top_spreads')}>
            {data.current.top_spreads_7d.length === 0 ? (
              <p className="text-xs italic text-gold-300/50">{t('admin.no_top_data')}</p>
            ) : (
              <ul className="w-full space-y-1.5">
                {data.current.top_spreads_7d.map((s) => (
                  <li key={s.kind} className="flex items-center justify-between
                                              rounded-lg border border-gold-500/20
                                              bg-night-900/40 px-3 py-2">
                    <span className="text-sm text-gold-200">
                      {t(`diary.kind_${s.kind}`, { defaultValue: s.kind })}
                    </span>
                    <span className="font-mono text-sm text-gold-400">{s.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={t('admin.section_activity')}>
            <HourlyHistogram data={data.current.activity_by_hour_7d} />
          </Section>

          {data.history.length > 1 && (
            <Section title={t('admin.section_history', { days: data.history.length })}>
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
                <Sparkline label={t('admin.history_dau')}       rows={data.history} field="dau" />
                <Sparkline label={t('admin.history_mau')}       rows={data.history} field="mau" />
                <Sparkline label={t('admin.history_priestess')} rows={data.history} field="priestess_active" />
                <Sparkline label={t('admin.history_stars_24h')} rows={data.history} field="stars_24h" />
              </div>
            </Section>
          )}

          {/* AI cost panel — fetches its own data so the rest of the
              dashboard renders even if the cost endpoint hiccups. */}
          <CostPanel token={token} />
        </>
      )}
    </main>
  );
}


// ─── Small presentational helpers (kept inline to avoid file sprawl) ─────

function Kpi({ label, value, tone }: {
  label: string; value: number | string;
  tone?: 'warn' | 'ok';
}) {
  const color = tone === 'warn'
    ? 'text-rose-300'
    : tone === 'ok' ? 'text-emerald-300' : 'text-gold-200';
  return (
    <div className="rounded-xl border border-gold-500/20 bg-night-900/50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-gold-500/60">
        {label}
      </div>
      <div className={`mt-1 font-mono text-lg ${color}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/60">
        {title}
      </h2>
      <div className="flex flex-wrap gap-3">{children}</div>
    </section>
  );
}

function HourlyHistogram({ data }: { data: number[] }) {
  const max = Math.max(1, ...data);
  return (
    <div className="w-full">
      <svg viewBox="0 0 240 80" className="h-24 w-full">
        {data.map((v, i) => {
          const h = (v / max) * 70;
          return (
            <g key={i}>
              <rect
                x={i * 10}
                y={75 - h}
                width={8}
                height={h}
                rx="1"
                fill="#e8c356"
                opacity={v > 0 ? 0.85 : 0.2}
              />
              {(i % 6 === 0) && (
                <text
                  x={i * 10 + 4}
                  y={79}
                  fontSize="4"
                  textAnchor="middle"
                  fill="#a78320"
                >{i}h</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Sparkline({ label, rows, field }: {
  label: string;
  rows: Array<Record<string, number | string>>;
  field: string;
}) {
  const series = rows.map((r) => Number(r[field] || 0));
  const max = Math.max(1, ...series);
  const points = series.map((v, i) => {
    const x = (i / Math.max(1, series.length - 1)) * 100;
    const y = 30 - (v / max) * 28;
    return `${x},${y}`;
  }).join(' ');
  const last = series[series.length - 1] ?? 0;
  return (
    <div className="rounded-xl border border-gold-500/20 bg-night-900/50 p-3">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest text-gold-500/60">
          {label}
        </div>
        <div className="font-mono text-base text-gold-200">{last}</div>
      </div>
      <svg viewBox="0 0 100 32" className="mt-1 h-12 w-full">
        <polyline
          points={points}
          fill="none"
          stroke="#e8c356"
          strokeWidth="0.8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}


/**
 * AI cost panel — independent from the main stats payload because the
 * data lifecycle is different (live aggregation over `ai_usage` rows
 * vs. snapshotted DAU history). Has its own loading / error state so
 * a wobbly cost query doesn't blank out the whole dashboard.
 *
 * The window selector lets us switch between 7/30/90-day views without
 * navigating away.
 */
function CostPanel({ token }: { token: string }) {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<AdminCostResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminCost(token, days)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, days]);

  return (
    <section className="mb-6 rounded-2xl border border-gold-500/20 bg-night-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[11px] uppercase tracking-widest text-gold-500/70">
          {t('admin.cost_title')}
        </h2>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={
                'rounded-full px-3 py-1 text-[10px] uppercase tracking-widest transition ' +
                (days === d
                  ? 'border border-gold-400 bg-gold-500/20 text-gold-100'
                  : 'border border-gold-500/30 text-gold-300/70 hover:border-gold-400')
              }
            >
              {t('admin.cost_window_days', { days: d })}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <p className="text-center text-xs text-gold-300/50">{t('admin.loading')}</p>
      )}
      {error && (
        <p className="text-center text-xs text-rose-400">{error}</p>
      )}

      {data && (
        <>
          {/* Totals grid */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi
              label={t('admin.cost_kpi_24h')}
              value={`$${data.totals.cost_24h_usd.toFixed(2)}`}
            />
            <Kpi
              label={t('admin.cost_kpi_7d')}
              value={`$${data.totals.cost_7d_usd.toFixed(2)}`}
            />
            <Kpi
              label={t('admin.cost_kpi_window', { days })}
              value={`$${data.totals.cost_usd.toFixed(2)}`}
            />
            <Kpi
              label={t('admin.cost_kpi_avg_user')}
              value={`$${data.totals.avg_cost_per_user_usd.toFixed(3)}`}
            />
            <Kpi
              label={t('admin.cost_kpi_tokens_in')}
              value={data.totals.tokens_in.toLocaleString()}
            />
            <Kpi
              label={t('admin.cost_kpi_tokens_out')}
              value={data.totals.tokens_out.toLocaleString()}
            />
            <Kpi
              label={t('admin.cost_kpi_active_users')}
              value={data.totals.users_active}
            />
          </div>

          {/* Trend chart */}
          {data.daily_series.length > 1 && (
            <div className="mb-5">
              <p className="mb-1 text-[10px] uppercase tracking-widest text-gold-500/60">
                {t('admin.cost_trend')}
              </p>
              <CostTrend series={data.daily_series} />
            </div>
          )}

          {/* By kind — table */}
          <div className="mb-5">
            <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('admin.cost_by_kind')}
            </p>
            {data.by_kind.length === 0 ? (
              <p className="text-xs italic text-gold-300/50">
                {t('admin.cost_empty')}
              </p>
            ) : (
              <ul className="space-y-1">
                {data.by_kind.map((k) => (
                  <li
                    key={k.kind}
                    className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3
                               rounded-lg border border-gold-500/20 bg-night-900/40
                               px-3 py-1.5 text-sm"
                  >
                    <span className="truncate font-mono text-gold-200">{k.kind}</span>
                    <span className="font-mono text-xs text-gold-300/70">
                      {k.calls} {t('admin.cost_calls_unit')}
                    </span>
                    <span className="font-mono text-xs text-gold-400/70">
                      {k.tokens.toLocaleString()}
                    </span>
                    <span className="font-mono text-sm text-gold-100">
                      ${k.cost_usd.toFixed(4)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Top users */}
          <div>
            <p className="mb-2 text-[10px] uppercase tracking-widest text-gold-500/60">
              {t('admin.cost_top_users')}
            </p>
            {data.top_users.length === 0 ? (
              <p className="text-xs italic text-gold-300/50">
                {t('admin.cost_empty')}
              </p>
            ) : (
              <ul className="space-y-1">
                {data.top_users.map((u) => (
                  <li
                    key={u.user_id}
                    className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3
                               rounded-lg border border-gold-500/20 bg-night-900/40
                               px-3 py-1.5 text-sm"
                  >
                    <span className="truncate text-gold-200">
                      {u.display_name}
                      {u.priestess && (
                        <span className="ml-2 rounded-full border border-gold-500/40
                                         px-1.5 text-[9px] uppercase tracking-widest
                                         text-gold-300">
                          ✦
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-gold-400/60">
                      #{u.user_id}
                    </span>
                    <span className="font-mono text-xs text-gold-300/70">
                      {u.calls} {t('admin.cost_calls_unit')}
                    </span>
                    <span className="font-mono text-sm text-gold-100">
                      ${u.cost_usd.toFixed(4)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}


/**
 * Tiny inline SVG line chart for daily cost. Same visual language as
 * the existing Sparkline but takes the cost-specific row shape.
 */
function CostTrend({ series }: { series: Array<{ day: string; cost_usd: number }> }) {
  if (series.length === 0) return null;
  const values = series.map((s) => s.cost_usd);
  const max = Math.max(...values, 0.0001);
  const points = values.map((v, i) => {
    const x = (i / Math.max(1, values.length - 1)) * 100;
    const y = 30 - (v / max) * 28;
    return `${x},${y}`;
  }).join(' ');
  const first = series[0].day.slice(5);     // 'MM-DD'
  const last = series[series.length - 1].day.slice(5);
  return (
    <div className="rounded-xl border border-gold-500/20 bg-night-900/40 p-3">
      <svg viewBox="0 0 100 32" className="h-16 w-full">
        <polyline
          points={points}
          fill="none"
          stroke="#e8c356"
          strokeWidth="0.8"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <div className="mt-1 flex justify-between text-[9px] text-gold-500/40">
        <span>{first}</span>
        <span>{last}</span>
      </div>
    </div>
  );
}
